import { noChange } from 'lit';
import { AsyncDirective } from 'lit/async-directive.js';
import {
  directive,
  type ElementPart,
  type PartInfo,
  PartType,
} from 'lit/directive.js';

import {
  type StreamLainAnimationController,
  streamLainAnimationController,
} from './streamlain-animation-controller';

/** A descendant that animates its own height already moves the natural layout. */
function hasResizingDescendant(surface: HTMLElement): boolean {
  return Array.from(surface.children).some(child =>
    child.getAnimations({ subtree: true }).some(animation => {
      const effect = animation.effect;
      return (
        effect instanceof KeyframeEffect &&
        effect.getKeyframes().some(frame => frame.height !== undefined)
      );
    })
  );
}

/**
 * Keeps an open disclosure surface (popover shell, disclosure body) from
 * jumping when its content changes size: the height moves from the last
 * settled value to the new one on the shared resize motion. `canResize`
 * lets an owner that reveals the same surface itself keep that motion.
 */
export function bindStreamLainDisclosureResize(
  surface: HTMLElement,
  options: {
    controller?: StreamLainAnimationController;
    /** The element whose size is watched; the surface itself by default. */
    content?: HTMLElement;
    canResize?: () => boolean;
  } = {}
): () => void {
  const controller = options.controller ?? streamLainAnimationController;
  const content = options.content ?? surface;
  const Observer = surface.ownerDocument.defaultView?.ResizeObserver;
  if (!Observer) return () => {};
  let settled: number | undefined;
  let resizing = false;
  let released = false;
  const observer = new Observer(() => {
    if (resizing || surface.hidden) return;
    const height = content.offsetHeight;
    const previous = settled;
    settled = height;
    if (
      previous === undefined ||
      previous <= 0 ||
      height <= 0 ||
      Math.abs(height - previous) <= 1 ||
      options.canResize?.() === false ||
      hasResizingDescendant(surface)
    )
      return;
    resizing = true;
    const surfaceOffset = surface.offsetHeight - height;
    void controller
      .resizeRegion(surface, previous + surfaceOffset, height + surfaceOffset)
      .finished.then(() => {
        resizing = false;
        if (released) return;
        // Content that changed again meanwhile is picked up from here.
        observer.unobserve(content);
        observer.observe(content);
      });
  });
  observer.observe(content);
  return () => {
    released = true;
    observer.disconnect();
    if (resizing) controller.cancel(surface);
  };
}

/** Bind after Lit has committed the surface; release with the owning part. */
class StreamLainDisclosureResizeDirective extends AsyncDirective {
  private element?: HTMLElement;
  private release?: () => void;

  constructor(part: PartInfo) {
    super(part);
    if (part.type !== PartType.ELEMENT) {
      throw new Error('Disclosure resize requires an element part');
    }
  }

  render() {
    return noChange;
  }

  override update(part: ElementPart) {
    if (!(part.element instanceof HTMLElement)) {
      throw new Error('Disclosure resize requires an HTML element');
    }
    if (this.element !== part.element) {
      this.disconnected();
      this.element = part.element;
    }
    if (!this.release) this.reconnected();
    return noChange;
  }

  protected override disconnected() {
    this.release?.();
    this.release = undefined;
  }

  protected override reconnected() {
    if (this.element && !this.release)
      this.release = bindStreamLainDisclosureResize(this.element);
  }
}

export const streamLainDisclosureResize = directive(
  StreamLainDisclosureResizeDirective
);
